/**
 * Platform Console WebSocket client.
 *
 * ── WHY THIS IS A SECOND CLIENT AND NOT A SECOND USE OF THE FIRST ───────────────────────────
 * The Platform Console authenticates with a different, non-interchangeable credential: a
 * PlatformAdmin JWT (`usePlatformAuthStore.accessToken`, issuer `jurivoxa-platform`) rather than
 * a chambers member JWT. The two sessions can even be open in the same browser at once. Sharing
 * `lib/websocket.ts` would mean one socket store holding one token for two identities — the
 * point at which a console tab starts connecting as whichever member happened to log in last.
 *
 * What IS shared is the only thing that must never disagree: the socket target, resolved once in
 * `lib/wsUrl.ts` and imported by both clients.
 *
 * ── WHAT IT CARRIES ─────────────────────────────────────────────────────────────────────────
 * Identifiers. The server puts this socket in one room (`user:platform:<id>`) and the only event
 * it receives is `support:ticket-updated` with `{ ticketId }`. No ticket body, no customer data,
 * no internal notes — the console re-reads everything through `platformApi`, which is where the
 * authorisation actually lives.
 *
 * ── LIFECYCLE ───────────────────────────────────────────────────────────────────────────────
 * Owned by PlatformShell, and by nothing else. A per-component connection (one per open
 * SupportConversation) would open and tear down sockets as drawers come and go, and would
 * re-authenticate on every navigation.
 */

import { io, Socket } from 'socket.io-client';
import { create } from 'zustand';
import { usePlatformAuthStore } from '@/features/platform-auth/store';
import { WS_URL } from './wsUrl';

interface PlatformWebSocketState {
  socket: Socket | null;
  connected: boolean;
  /** No-op when there is no platform access token yet — the shell retries when one arrives. */
  connect: () => void;
  disconnect: () => void;
}

export const usePlatformWebSocketStore = create<PlatformWebSocketState>()((set, get) => ({
  socket: null,
  connected: false,

  connect: () => {
    const existing = get().socket;
    if (existing?.connected) return;

    // The platform access token is held in memory only and is null until the first
    // `/auth/refresh` succeeds. That is not an error — the shell calls connect() again when the
    // token lands, so this stays silent rather than logging a warning on every console load.
    const token = usePlatformAuthStore.getState().accessToken;
    if (!token) return;

    // A stale instance may exist but be disconnected (e.g. after a token change). Drop it before
    // opening a new one, or the old instance keeps retrying in the background forever.
    if (existing) existing.disconnect();

    const socket = io(WS_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      // Same reasoning as the member client: a finite attempt count means a console left open
      // across an API restart goes permanently stale with nothing to re-arm it.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => set({ connected: true }));
    socket.on('disconnect', () => set({ connected: false }));
    socket.on('connect_error', (err) => {
      console.warn('Platform WebSocket connection error:', err.message);
      set({ connected: false });
    });

    set({ socket });
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, connected: false });
    }
  },
}));

/** Identifier only — the console refetches the ticket over HTTP. */
export type PlatformSupportUpdateEvent = {
  ticketId: string;
};

/**
 * Subscribe to support ticket updates for the signed-in platform admin.
 *
 * Like the member side, this refreshes the server-derived queue; it carries no notion of "read"
 * and never clears the unanswered badge by itself.
 */
export function onPlatformSupportUpdate(
  callback: (data: PlatformSupportUpdateEvent) => void,
): () => void {
  const { socket } = usePlatformWebSocketStore.getState();
  if (!socket) return () => {};
  socket.on('support:ticket-updated', callback);
  return () => socket.off('support:ticket-updated', callback);
}
